import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ShieldCheck,
  Plus,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronRight,
  RefreshCw,
} from 'lucide-react'
import { useAuthStore } from '@personic/shared'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

interface Attestation {
  id: string
  framework: string
  status: string
  attested_by_name: string
  attested_by_title: string
  submitted_at: string
  expires_at: string
  is_expired: boolean
  is_active: boolean
  days_until_expiry: number
}

// Framework display info
const FRAMEWORK_INFO: Record<string, { name: string; fullName: string; product: string }> = {
  hipaa: { name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act', product: 'Clinical' },
  eeoc: { name: 'EEOC', fullName: 'Equal Employment Opportunity Commission', product: 'Talent' },
  batson: { name: 'Batson', fullName: 'Batson v. Kentucky Compliance', product: 'Legal' },
  ferpa: { name: 'FERPA', fullName: 'Family Educational Rights and Privacy Act', product: 'Education' },
  finra: { name: 'FINRA', fullName: 'Financial Industry Regulatory Authority', product: 'Finance' },
}

export default function Attestations() {
  const { accessToken } = useAuthStore()
  const [attestations, setAttestations] = useState<Attestation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAttestations = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE_URL}/attestations?include_expired=true`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch attestations')
      }

      const data = await response.json()
      setAttestations(data.attestations)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAttestations()
  }, [accessToken])

  const getStatusBadge = (attestation: Attestation) => {
    if (attestation.is_expired) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3.5 h-3.5" />
          Expired
        </span>
      )
    }

    switch (attestation.status) {
      case 'approved':
        return attestation.days_until_expiry <= 30 ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <AlertTriangle className="w-3.5 h-3.5" />
            Expiring Soon
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            Active
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Clock className="w-3.5 h-3.5" />
            Pending Review
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3.5 h-3.5" />
            Rejected
          </span>
        )
      default:
        return null
    }
  }

  // Group attestations by status
  const activeAttestations = attestations.filter(
    (a) => a.status === 'approved' && !a.is_expired
  )
  const pendingAttestations = attestations.filter((a) => a.status === 'pending')
  const expiredAttestations = attestations.filter(
    (a) => a.is_expired || a.status === 'rejected'
  )
  const expiringAttestations = activeAttestations.filter(
    (a) => a.days_until_expiry <= 30
  )

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attestations</h1>
          <p className="text-gray-500 mt-1">
            Manage your regulatory compliance attestations
          </p>
        </div>
        <Link
          to="/tenant/attestations/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Attestation
        </Link>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchAttestations}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Expiring Soon Warning */}
          {expiringAttestations.length > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-800">
                    Attestations Expiring Soon
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    {expiringAttestations.length} attestation(s) will expire within
                    the next 30 days. Please renew them to maintain access to
                    regulated features.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {attestations.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
              <ShieldCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Attestations Yet
              </h3>
              <p className="text-gray-500 mb-4">
                Create your first regulatory compliance attestation to unlock
                regulated features.
              </p>
              <Link
                to="/tenant/attestations/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Attestation
              </Link>
            </div>
          )}

          {/* Active Attestations */}
          {activeAttestations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Active Attestations
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
                {activeAttestations.map((attestation) => (
                  <AttestationRow
                    key={attestation.id}
                    attestation={attestation}
                    statusBadge={getStatusBadge(attestation)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pending Attestations */}
          {pendingAttestations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Pending Review
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
                {pendingAttestations.map((attestation) => (
                  <AttestationRow
                    key={attestation.id}
                    attestation={attestation}
                    statusBadge={getStatusBadge(attestation)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Expired/Rejected Attestations */}
          {expiredAttestations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Expired / Rejected
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200 opacity-60">
                {expiredAttestations.map((attestation) => (
                  <AttestationRow
                    key={attestation.id}
                    attestation={attestation}
                    statusBadge={getStatusBadge(attestation)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Attestation row component
function AttestationRow({
  attestation,
  statusBadge,
}: {
  attestation: Attestation
  statusBadge: React.ReactNode
}) {
  const info = FRAMEWORK_INFO[attestation.framework]

  return (
    <Link
      to={`/tenant/attestations/${attestation.id}`}
      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-personic-100 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-personic-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">
              {info?.name || attestation.framework.toUpperCase()}
            </h3>
            <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">
              {info?.product}
            </span>
          </div>
          <p className="text-sm text-gray-500">{info?.fullName}</p>
          <p className="text-xs text-gray-400 mt-1">
            Attested by {attestation.attested_by_name} ({attestation.attested_by_title})
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {statusBadge}
        <div className="text-right text-xs text-gray-500">
          {attestation.status === 'approved' && !attestation.is_expired && (
            <p>Expires in {attestation.days_until_expiry} days</p>
          )}
          {attestation.status === 'pending' && (
            <p>
              Submitted{' '}
              {new Date(attestation.submitted_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </Link>
  )
}
